import { Avatar, Button, Checkbox, CloseButton, Divider, Flex, Grid, LoadingOverlay, Text } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { RequestApi } from "../../apis";
import noimage from "../../assets/no-image.png";
import { IBusiness } from "../../interfaces/IBusiness";
import { IGeneral } from "../../interfaces/IGeneral";
import HttpHandler from "../../utils/http-handler";
import { Message } from "../../utils/message";
import { Form } from "../form";
import { RadioGroupField } from "../form/radio-group-field";
import { TextField } from "../form/text-field";
import { FormLayout } from "../layout";
import { ColumnType, Table } from "../table";
import RenderText from "../ui/render-text";

const schema = yup.object({
  clientType: yup.string().required("Заавал бөглөнө!").nullable(),
});

type IFormData = {
  status?: string;
  partnerId: string;
  clientType: string;
  regNumber: string;
  businessName: string;
  refCode: string;
};

const status = [
  { code: "success", name: "Зөв таарч байна" },
  { code: "not", name: "Таарахгүй байна" },
];

export function RequestForm({ action, onCancel, reloadTab }: { action: any; onCancel: () => void; reloadTab: () => void }) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const { clientTypes } = useSelector((state: { general: IGeneral }) => state.general);

  const columns = useHeader();

  const onSubmit = async (values: IFormData) => {
    setLoading(true);
    try {
      await RequestApi.responds({ partnerId: action && action[1]?.partnerId, clientType: values.clientType });
      reloadTab();
      onCancel && onCancel();
      Message.success(
        "Та харилцагчийг санхүүжилтийн үйлчилгээнд хамрах боломжтой болгож зөвшөөрлөө. Банктай харилцаж ажиллах ажилтанд эрх зөвшөөрөх үйлдэл хийнэ үү.",
      );
    } catch (err) {
      Message.error((err as HttpHandler)?.message!);
    }
    setLoading(false);
  };

  const regNumber = action?.[1]?.partner?.regNumber;

  const [userData, setUserData] = useState(Object.create({}));
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!regNumber) {
        setUserData(Object.create({}));
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const res = await RequestApi.info(regNumber);
        setUserData(res);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [regNumber]);

  if (isLoading || loading) return <LoadingOverlay visible={isLoading || loading} />;

  return (
    <Form
      key={action[1]?.partner?.regNumber}
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={{
        ...userData,
        ...action[1],
        status: "",
        partnerId: "",
        clientType: "",
        businessName: action[1]?.partner?.businessName,
        refCode: action[1]?.partner?.refCode,
        regNumber: action[1]?.partner?.regNumber,
        infoRegNumber: userData?.regNumber,
        infoCorpKey: userData?.corpKey,
        infoLegalEntityType: userData?.legalEntityType,
        infoCorpName: userData?.corpName,
      }}>
      {({ values }) => {
        return (
          <FormLayout
            title="Хүсэлт зөвшөөрөх"
            subTitle="Supply Chain Finance үйлчилгээнд хамрагдах хүсэлт"
            my={0}
            extra={[
              <CloseButton
                key="cancel"
                onClick={() => {
                  onCancel && onCancel();
                }}
              />,
            ]}>
            <Divider />

            <Flex direction="column">
              <Grid>
                <Grid.Col span={12}>
                  <Text fw={500} color="gray" size={16} mt="xs">
                    Харилцагчийн банкны бүртгэл дэх мэдээлэл
                  </Text>
                </Grid.Col>

                <Grid.Col span={4}>
                  <TextField name="infoRegNumber" disabled label="Татвар төлөгчийн дугаар: " placeholder="Нэр: " withAsterisk={false} />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextField name="infoCorpName" disabled label="Харилцагчийн нэр: " placeholder="Нэр: " withAsterisk={false} />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextField name="infoCorpKey" disabled label="Харилцагчийн код: " placeholder="Код: " withAsterisk={false} />
                </Grid.Col>

                <Grid.Col span={4}>
                  <TextField name="infoLegalEntityType" disabled label="Аж ахуй нэгжийн хэлбэр: " placeholder="ААН: " withAsterisk={false} />
                </Grid.Col>
                <Grid.Col span={4}></Grid.Col>
                <Grid.Col span={4}></Grid.Col>

                <Grid.Col span={12}>
                  <Text fw={500} color="gray" size={16}>
                    DeHUB платформ дахь мэдээлэл
                  </Text>
                </Grid.Col>

                <Grid.Col span={4}>
                  <TextField name="regNumber" disabled label="Татвар төлөгчийн дугаар: " placeholder="ТТД: " withAsterisk={false} />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextField name="refCode" label="Партнер код: " placeholder="Код: " withAsterisk={false} disabled />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextField name="businessName" label="Партнер нэр: " placeholder="Нэр: " withAsterisk={false} disabled />
                </Grid.Col>

                <Grid.Col span={12}>
                  <Divider />
                  <Table name="businesses.list" columns={columns} dataSource={action[1]?.businesses || []} pagination={false} />
                  <Divider />
                </Grid.Col>

                <Grid.Col span={12}>
                  <Text fw={500} color="gray" size={16}>
                    Баталгаажилт хэсэг
                  </Text>
                </Grid.Col>
                <Grid.Col span={5}>
                  <RadioGroupField
                    label="Платформ дээрх мэдээлэл нь банкны мэдээлэлтэй"
                    name="status"
                    withAsterisk={false}
                    options={(status || []).map((c) => ({
                      label: c.name,
                      value: c.code,
                    }))}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <RadioGroupField
                    label="Харилцагчийн төрөл"
                    name="clientType"
                    options={(clientTypes || []).map((c) => ({
                      label: c.name,
                      value: c.code,
                    }))}
                  />
                </Grid.Col>
              </Grid>
            </Flex>
            <Divider mt="md" />
            <Flex mt={20} direction="row" justify="space-between">
              <Button variant="default" onClick={() => {}}>
                Болих
              </Button>
              <Flex gap="md">
                <Button type="submit" disabled={values.status !== "success"}>
                  Урилга зөвшөөрөх
                </Button>
              </Flex>
            </Flex>
          </FormLayout>
        );
      }}
    </Form>
  );
}

const useHeader = (): ColumnType<IBusiness>[] => [
  {
    title: "#",
    width: 30,
    render: (_record, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Зураг",
    render: (record) => <Avatar size={40} src={record?.logo !== null ? record?.logo : noimage} radius={5} />,
  },
  {
    title: "Бизнес код",
    dataIndex: "refCode",
    render: (record) => {
      return <RenderText text={record.refCode ?? "-"} />;
    },
  },
  {
    title: "Бизнес нэр",
    dataIndex: "profileName",
    render: (record) => {
      return <RenderText text={record.profileName ?? "-"} />;
    },
  },
  {
    title: "Buyer",
    render: (record) => {
      return (
        <Checkbox
          checked={record.type === "BUYER" ? true : false}
          label={
            <Text size="sm" weight={500} c="#44566C">
              {record.type === "BUYER" ? "Тийм" : "Үгүй"}
            </Text>
          }
          size="sm"
          onChange={() => {}}
        />
      );
    },
  },
  {
    title: "Supplier",
    render: (record) => {
      return (
        <Checkbox
          checked={record.type === "SUPPLIER" ? true : false}
          label={
            <Text size="sm" weight={500} c="#44566C">
              {record.type === "SUPPLIER" ? "Тийм" : "Үгүй"}
            </Text>
          }
          size="sm"
          onChange={() => {}}
        />
      );
    },
  },
];
